import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/utils-image";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Loader2, Images } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FileActionSheet } from "../components/FileActionSheet";
import { CategoryPie } from "../components/CategoryPie";
import { categoryHex, categoryLabel } from "../lib/categories";
import { useCategories } from "../lib/useCategories";
import { isMobileDevice, downloadBlob, formatFileSize } from "../lib/file-delivery";

const monthsAr = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

export default function ReportsPage() {
  useCategories();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [invoices, setInvoices] = useState([]);
  const [currency, setCurrency] = useState("SAR");
  const [monthSummary, setMonthSummary] = useState(null);  // { opening_balance, total_expense, total_tax, total_discount, closing_balance, count }
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [readyFile, setReadyFile] = useState(null); // {blob, filename, mime}

  // Mobile → show a native share/preview sheet. Desktop → direct download.
  const deliverFile = (blob, filename, mime, successMsg) => {
    if (isMobileDevice()) {
      setReadyFile({ blob, filename, mime });
    } else {
      downloadBlob(blob, filename);
    }
    const sizeText = formatFileSize(blob.size);
    toast.success(sizeText ? `${successMsg} (${sizeText})` : successMsg);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (month !== "all") params.month = month;
      if (year !== "all") params.year = year;
      const reqs = [
        // Light mode — page renders fast (no Base64 images). Images are
        // fetched on-demand only when user clicks an export that needs them.
        api.get("/invoices", { params: { ...params, light: true } }),
        api.get("/settings"),
      ];
      if (month !== "all" && year !== "all") {
        reqs.push(api.get("/reports/monthly", { params: { month, year } }));
      } else {
        reqs.push(Promise.resolve({ data: null }));
      }
      const [r, s, m] = await Promise.all(reqs);
      const sorted = [...(r.data || [])].sort((a, b) =>
        (a.invoice_date || "").localeCompare(b.invoice_date || "")
      );
      setInvoices(sorted);
      setCurrency(s.data?.currency || "SAR");
      setMonthSummary(m.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) toast.error("انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى");
      else if (!err?.response) toast.error("تعذر الاتصال بالخادم");
      else toast.error(detail || `تعذر تحميل التقرير (خطأ ${status || ""})`);
    } finally {
      setLoading(false);
    }
  };

  // Fetches the full invoices list WITH image_base64 — called on-demand
  // by exports that need images (PDF with images / images-only PDF).
  const fetchInvoicesWithImages = async () => {
    const params = {};
    if (month !== "all") params.month = month;
    if (year !== "all") params.year = year;
    const r = await api.get("/invoices", { params });
    return [...(r.data || [])].sort((a, b) =>
      (a.invoice_date || "").localeCompare(b.invoice_date || "")
    );
  };

  useEffect(() => { load(); }, [month, year]); // eslint-disable-line

  const totals = useMemo(() => {
    const total = invoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);
    const tax = invoices.reduce((s, i) => s + Number(i.tax_amount || 0), 0);
    const disc = invoices.reduce((s, i) => s + Number(i.discount_amount || 0), 0);
    return { total, tax, disc, count: invoices.length };
  }, [invoices]);

  const categoryBreakdown = useMemo(() => {
    const agg = {};
    invoices.forEach((i) => {
      const cat = i.category || "أخرى";
      agg[cat] = agg[cat] || { category: cat, total: 0, count: 0 };
      agg[cat].total += Number(i.total_amount || 0);
      agg[cat].count += 1;
    });
    return Object.values(agg).sort((a, b) => b.total - a.total);
  }, [invoices]);

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => y - i);
  }, []);

  const exportExcel = async () => {
    setExporting("excel");
    try {
      const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

      // Brand palette (matches the app)
      const DARK   = "FF1F3A33";   // deep emerald
      const DARK2  = "FF15281F";
      const OLIVE  = "FF6A8A76";
      const SAGE   = "FFA3C4B0";
      const CREAM  = "FFF5F4EF";
      const CREAM2 = "FFFAF9F5";
      const INK    = "FF1A1C19";
      const MUTED  = "FF8A8D86";
      const RED    = "FFC84C31";
      const RED_BG = "FFFBE9E3";
      const WHITE  = "FFFFFFFF";

      const wb = new ExcelJS.Workbook();
      wb.creator = "Fawateery";
      wb.created = new Date();
      const ws = wb.addWorksheet(periodLabel().slice(0, 30), {
        views: [{ rightToLeft: true, state: "frozen", ySplit: 0, showGridLines: false }],
        properties: { defaultRowHeight: 20 },
      });

      // ---- Column widths (12 columns) ----
      ws.columns = [
        { width: 6  }, // م
        { width: 13 }, // التاريخ
        { width: 9  }, // الوقت
        { width: 26 }, // اسم المتجر
        { width: 18 }, // رقم الفاتورة
        { width: 16 }, // إجمالي الفاتورة
        { width: 13 }, // الضريبة
        { width: 13 }, // الخصم
        { width: 16 }, // طريقة الدفع
        { width: 17 }, // الرصيد قبل
        { width: 17 }, // الرصيد بعد
        { width: 30 }, // ملاحظات
      ];

      const lastCol = 12;
      const colLetter = (n) => ws.getColumn(n).letter;
      const range = (r, c1, c2) => `${colLetter(c1)}${r}:${colLetter(c2)}${r}`;

      // ===== Row 1 — Brand band =====
      ws.mergeCells(range(1, 1, lastCol));
      const r1 = ws.getRow(1);
      r1.height = 44;
      const c1 = ws.getCell(1, 1);
      c1.value = "فواتيري · FAWATEERY";
      c1.font = { name: "Calibri", size: 22, bold: true, color: { argb: WHITE } };
      c1.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
      c1.fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };

      // ===== Row 2 — Sub-title =====
      ws.mergeCells(range(2, 1, lastCol));
      const r2 = ws.getRow(2);
      r2.height = 26;
      const c2 = ws.getCell(2, 1);
      c2.value = `تقرير محاسبي شهري — ${periodLabel()}`;
      c2.font = { name: "Calibri", size: 12, bold: true, color: { argb: WHITE } };
      c2.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
      c2.fill = { type: "pattern", pattern: "solid", fgColor: { argb: OLIVE } };

      // ===== Row 3 — Generated on =====
      ws.mergeCells(range(3, 1, lastCol));
      const r3 = ws.getRow(3);
      r3.height = 18;
      const c3 = ws.getCell(3, 1);
      c3.value = `تاريخ الإصدار: ${new Date().toLocaleString("ar-SA-u-nu-latn")} · العملة: ${currency}`;
      c3.font = { name: "Calibri", size: 10, italic: true, color: { argb: MUTED } };
      c3.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
      c3.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CREAM2 } };

      // Row 4 — spacer
      ws.getRow(4).height = 8;

      // ===== Rows 5-8 — KPI Cards (3 cards across 12 columns: 4 cols each) =====
      // Card layout: label row (5), value row (6,7 merged), unit row (8)
      const drawKpi = (startCol, label, value, accent, isNegative = false) => {
        const endCol = startCol + 3;

        // label
        ws.mergeCells(range(5, startCol, endCol));
        const lc = ws.getCell(5, startCol);
        lc.value = label;
        lc.font = { name: "Calibri", size: 10, bold: true, color: { argb: WHITE } };
        lc.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
        lc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: accent } };
        ws.getRow(5).height = 22;

        // value (merge rows 6+7)
        ws.mergeCells(`${colLetter(startCol)}6:${colLetter(endCol)}7`);
        const vc = ws.getCell(6, startCol);
        vc.value = round2(value);
        vc.numFmt = `#,##0.00 "${currency}"`;
        vc.font = { name: "Calibri", size: 22, bold: true, color: { argb: isNegative ? RED : INK } };
        vc.alignment = { vertical: "middle", horizontal: "center" };
        vc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CREAM } };
        ws.getRow(6).height = 26;
        ws.getRow(7).height = 26;

        // unit / sub-label (row 8)
        ws.mergeCells(range(8, startCol, endCol));
        const uc = ws.getCell(8, startCol);
        uc.value = isNegative ? "عجز يُرحَّل سالباً" : "";
        uc.font = { name: "Calibri", size: 9, italic: true, color: { argb: isNegative ? RED : MUTED } };
        uc.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
        uc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CREAM } };
        ws.getRow(8).height = 18;

        // Outer borders for the card
        for (let r = 5; r <= 8; r++) {
          for (let c = startCol; c <= endCol; c++) {
            const cell = ws.getCell(r, c);
            cell.border = {
              top:    r === 5 ? { style: "medium", color: { argb: accent } } : undefined,
              bottom: r === 8 ? { style: "medium", color: { argb: accent } } : undefined,
              left:   c === startCol ? { style: "medium", color: { argb: accent } } : undefined,
              right:  c === endCol   ? { style: "medium", color: { argb: accent } } : undefined,
            };
          }
        }
      };

      drawKpi(1, "الرصيد المتاح (يشمل الدعم)", monthOpening, OLIVE);
      drawKpi(5, "إجمالي المصروفات", totals.total, RED);
      drawKpi(9, monthClosing >= 0 ? "المتبقي · يُرحَّل" : "العجز · يُرحَّل سالباً",
              monthClosing, monthClosing >= 0 ? DARK : RED, monthClosing < 0);

      ws.mergeCells("A9:L9");
      ws.getCell("A9").value = monthSummary
        ? `الافتتاحي: ${monthSummary.opening_base} | الدعم: ${monthSummary.support_total || 0} | إجمالي التمويل: ${monthSummary.funding_total ?? monthSummary.opening_base} | المرحّل: ${monthSummary.carry_over}`
        : "";
      ws.getCell("A9").alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      ws.getCell("A9").font = { name: "Calibri", size: 10 };
      ws.getRow(9).height = 30;

      // ===== Rows 10-11 — Mini-stats strip (count / tax / discount) =====
      const miniRow = 10;
      const drawMini = (startCol, label, value, isCount = false) => {
        const endCol = startCol + 3;
        ws.mergeCells(range(miniRow, startCol, endCol));
        const lc = ws.getCell(miniRow, startCol);
        lc.value = label;
        lc.font = { name: "Calibri", size: 9, bold: true, color: { argb: MUTED } };
        lc.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
        lc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CREAM2 } };

        ws.mergeCells(range(miniRow + 1, startCol, endCol));
        const vc = ws.getCell(miniRow + 1, startCol);
        vc.value = isCount ? Number(value) : round2(value);
        vc.numFmt = isCount ? "0" : `#,##0.00 "${currency}"`;
        vc.font = { name: "Calibri", size: 14, bold: true, color: { argb: INK } };
        vc.alignment = { vertical: "middle", horizontal: "center" };
        vc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CREAM2 } };

        for (let r = miniRow; r <= miniRow + 1; r++) {
          for (let c = startCol; c <= endCol; c++) {
            ws.getCell(r, c).border = {
              top:    r === miniRow     ? { style: "thin", color: { argb: "FFE0DFD8" } } : undefined,
              bottom: r === miniRow + 1 ? { style: "thin", color: { argb: "FFE0DFD8" } } : undefined,
              left:   c === startCol    ? { style: "thin", color: { argb: "FFE0DFD8" } } : undefined,
              right:  c === endCol      ? { style: "thin", color: { argb: "FFE0DFD8" } } : undefined,
            };
          }
        }
      };
      drawMini(1, "عدد الفواتير", totals.count, true);
      drawMini(5, "إجمالي الضريبة", totals.tax);
      drawMini(9, "إجمالي الخصم", totals.disc);
      ws.getRow(miniRow).height = 18;
      ws.getRow(miniRow + 1).height = 22;

      // Row 12 — spacer
      ws.getRow(12).height = 10;

      // ===== Row 13 — Section title "سجل الفواتير" =====
      ws.mergeCells(range(13, 1, lastCol));
      const sec = ws.getCell(13, 1);
      sec.value = "سجل الفواتير التفصيلي";
      sec.font = { name: "Calibri", size: 14, bold: true, color: { argb: WHITE } };
      sec.alignment = { vertical: "middle", horizontal: "right", readingOrder: "rtl", indent: 1 };
      sec.fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK2 } };
      ws.getRow(13).height = 28;

      // ===== Row 14 — Table headers =====
      const headerRowNum = 14;
      const headers = [
        "م", "التاريخ", "الوقت", "اسم المتجر", "رقم الفاتورة",
        "إجمالي الفاتورة", "الضريبة", "الخصم", "طريقة الدفع",
        "الرصيد قبل", "الرصيد بعد", "ملاحظات",
      ];
      const hRow = ws.getRow(headerRowNum);
      headers.forEach((h, i) => {
        const cell = hRow.getCell(i + 1);
        cell.value = h;
        cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: WHITE } };
        cell.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl", wrapText: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
        cell.border = {
          top:    { style: "thin", color: { argb: DARK2 } },
          bottom: { style: "medium", color: { argb: OLIVE } },
          left:   { style: "thin", color: { argb: DARK2 } },
          right:  { style: "thin", color: { argb: DARK2 } },
        };
      });
      hRow.height = 32;

      // Freeze panes so header stays visible
      ws.views = [{ rightToLeft: true, state: "frozen", ySplit: headerRowNum, showGridLines: false }];

      // ===== Data rows =====
      let r = headerRowNum + 1;
      invoices.forEach((inv, idx) => {
        const row = ws.getRow(r);
        const isZebra = idx % 2 === 1;
        const balAfter = round2(inv.balance_after || 0);
        const isNeg = balAfter < 0;

        const values = [
          idx + 1,
          inv.invoice_date || "",
          inv.invoice_time || "",
          inv.store_name || "",
          inv.invoice_number || "",
          round2(inv.total_amount),
          round2(inv.tax_amount),
          round2(inv.discount_amount),
          inv.payment_method || "",
          round2(inv.balance_before),
          balAfter,
          inv.notes || "",
        ];
        values.forEach((v, i) => {
          const cell = row.getCell(i + 1);
          cell.value = v;
          cell.font = { name: "Calibri", size: 10, color: { argb: INK } };
          cell.alignment = {
            vertical: "middle",
            horizontal: (i === 0 || (i >= 5 && i <= 7) || i === 9 || i === 10) ? "center" : "right",
            readingOrder: "rtl",
            wrapText: true,
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: isZebra ? CREAM : WHITE },
          };
          cell.border = {
            top:    { style: "hair", color: { argb: "FFE0DFD8" } },
            bottom: { style: "hair", color: { argb: "FFE0DFD8" } },
            left:   { style: "hair", color: { argb: "FFE0DFD8" } },
            right:  { style: "hair", color: { argb: "FFE0DFD8" } },
          };
        });

        // Number format for monetary columns: 6,7,8,10,11
        [6, 7, 8, 10, 11].forEach((ci) => {
          row.getCell(ci).numFmt = `#,##0.00 "${currency}"`;
        });
        // Bold key columns
        row.getCell(6).font = { name: "Calibri", size: 10, bold: true, color: { argb: INK } };
        row.getCell(11).font = {
          name: "Calibri", size: 10, bold: true,
          color: { argb: isNeg ? RED : DARK },
        };
        if (isNeg) {
          row.getCell(11).fill = { type: "pattern", pattern: "solid", fgColor: { argb: RED_BG } };
        }
        row.height = 22;
        r += 1;
      });

      // ===== Totals row =====
      const totalsRow = ws.getRow(r);
      totalsRow.height = 26;
      const tCells = {
        1: "",
        2: "",
        3: "",
        4: `الإجمالي (${totals.count} فاتورة)`,
        5: "",
        6: round2(totals.total),
        7: round2(totals.tax),
        8: round2(totals.disc),
        9: "",
        10: "",
        11: round2(monthClosing),
        12: "",
      };
      Object.entries(tCells).forEach(([col, val]) => {
        const c = totalsRow.getCell(Number(col));
        c.value = val;
        c.font = { name: "Calibri", size: 11, bold: true, color: { argb: WHITE } };
        c.alignment = {
          vertical: "middle",
          horizontal: Number(col) === 4 ? "right" : "center",
          readingOrder: "rtl",
          indent: Number(col) === 4 ? 1 : 0,
        };
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
        c.border = {
          top:    { style: "medium", color: { argb: OLIVE } },
          bottom: { style: "medium", color: { argb: OLIVE } },
        };
      });
      [6, 7, 8, 11].forEach((ci) => {
        totalsRow.getCell(ci).numFmt = `#,##0.00 "${currency}"`;
      });
      if (monthClosing < 0) {
        totalsRow.getCell(11).fill = { type: "pattern", pattern: "solid", fgColor: { argb: RED } };
      }
      r += 1;

      // Footer
      ws.getRow(r).height = 8;
      r += 1;
      ws.mergeCells(range(r, 1, lastCol));
      const foot = ws.getCell(r, 1);
      foot.value = "© فواتيري · Fawateery — تقرير مُولَّد تلقائياً";
      foot.font = { name: "Calibri", size: 9, italic: true, color: { argb: MUTED } };
      foot.alignment = { vertical: "middle", horizontal: "center", readingOrder: "rtl" };
      foot.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CREAM2 } };
      ws.getRow(r).height = 20;

      // Print setup (nice for printing/exporting from Excel directly)
      ws.pageSetup = {
        paperSize: 9, // A4
        orientation: "landscape",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        horizontalCentered: true,
        margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
      };
      ws.headerFooter = {
        oddFooter: "&C&\"Calibri\"&9 صفحة &P من &N",
      };

      const buf = await wb.xlsx.writeBuffer();
      const xlsxBlob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const xlsxName = `Fawateery_${fileSuffix()}.xlsx`;
      if (isMobileDevice()) {
        deliverFile(xlsxBlob, xlsxName, xlsxBlob.type, "تم تصدير ملف Excel احترافي");
      } else {
        saveAs(xlsxBlob, xlsxName);
        toast.success("تم تصدير ملف Excel احترافي");
      }
    } catch (e) {
      console.error(e);
      toast.error("تعذر تصدير Excel");
    } finally {
      setExporting(null);
    }
  };

  const periodLabel = () => {
    if (month === "all" && year === "all") return "كل الفترات / All Periods";
    if (month === "all") return `${year} / All months ${year}`;
    if (year === "all") return `${monthsAr[month - 1]} / All years`;
    return `${monthsAr[month - 1]} ${year}`;
  };

  const fileSuffix = () => {
    if (month === "all" && year === "all") return "All_Periods";
    if (month === "all") return `${year}_All`;
    if (year === "all") return `All_${String(month).padStart(2, "0")}`;
    return `${year}_${String(month).padStart(2, "0")}`;
  };

  const monthOpening = monthSummary?.opening_balance ?? 0;
  const monthClosing = monthSummary?.closing_balance ?? (monthOpening - (monthSummary?.total_expense ?? 0));

  const exportPDF = async (includeImages = true) => {
    setExporting(includeImages ? "pdf" : "pdf-light");
    try {
      // Fetch heavy data (with images) only when needed.
      let dataset = invoices;
      if (includeImages && invoices.length && !invoices.some((i) => i.image_base64)) {
        dataset = await fetchInvoicesWithImages();
      }
      // Wait for web fonts (Readex Pro / Tajawal) to be ready so Arabic renders crisply.
      try { await document.fonts?.ready; } catch (e) { /* fonts API not available */ }

      // ============================================================
      // 1) Build an off-screen RTL Arabic HTML report
      // ============================================================
      const W_PX = 794;        // A4 width @ 96dpi
      const PAGE_PX = 1123;    // A4 height @ 96dpi
      const root = document.createElement("div");
      root.setAttribute("dir", "rtl");
      root.style.cssText = `
        position: fixed; top: -10000px; right: 0; width: ${W_PX}px;
        background: #FFFFFF; color: #1A1C19;
        font-family: 'IBM Plex Sans Arabic','Readex Pro','Noto Sans Arabic',Arial,sans-serif;
        line-height: 1.7; -webkit-font-smoothing: antialiased;
        font-weight: 400;
        font-feature-settings: "kern" 1, "liga" 1, "calt" 1;
      `;
      // Use Western digits (clearer for financial reports, no Eastern-Arabic confusion).
      const fmt = (n) => Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
      const periodAr = periodLabel();
      const negative = monthClosing < 0;

      // Build invoice rows HTML
      const rowsHtml = dataset.map((inv, i) => {
        const bal = Number(inv.balance_after || 0);
        const balCls = bal < 0
          ? "background:#FBE9E3;color:#C84C31;font-weight:600;"
          : "color:#1F3A33;font-weight:600;";
        const zebra = i % 2 === 1 ? "background:#FAF9F5;" : "background:#FFFFFF;";
        return `
          <tr style="${zebra}">
            <td style="padding:7px 4px;text-align:center;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;">${i + 1}</td>
            <td style="padding:7px 4px;text-align:center;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;white-space:nowrap;">${escapeHtml(inv.invoice_date || "")}</td>
            <td style="padding:7px 6px;text-align:right;border:1px solid #E0DFD8;font-weight:400;">${escapeHtml(inv.store_name || "")}</td>
            <td style="padding:7px 4px;text-align:center;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;color:#5C605A;">${escapeHtml(inv.invoice_number || "")}</td>
            <td style="padding:7px 4px;text-align:left;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;direction:ltr;font-weight:600;">${fmt(inv.total_amount)}</td>
            <td style="padding:7px 4px;text-align:left;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;direction:ltr;color:#5C605A;">${fmt(inv.tax_amount)}</td>
            <td style="padding:7px 4px;text-align:left;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;direction:ltr;color:#5C605A;">${fmt(inv.discount_amount)}</td>
            <td style="padding:7px 4px;text-align:center;border:1px solid #E0DFD8;color:#5C605A;font-size:10px;font-weight:400;">${escapeHtml(inv.payment_method || "")}</td>
            <td style="padding:7px 4px;text-align:left;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;direction:ltr;color:#5C605A;">${fmt(inv.balance_before)}</td>
            <td style="padding:7px 4px;text-align:left;border:1px solid #E0DFD8;font-variant-numeric:tabular-nums;direction:ltr;${balCls}">${fmt(inv.balance_after)}</td>
          </tr>`;
      }).join("");

      const notesHtml = dataset.filter(i => (i.notes || "").trim()).map((inv, i) => `
        <div style="padding:8px 12px;border-bottom:1px dashed #E5E4DC;font-size:12px;">
          <span style="color:#8A8D86;margin-left:8px;">${escapeHtml(inv.invoice_date || "")} · ${escapeHtml(inv.store_name || "")}:</span>
          <span style="color:#1A1C19;">${escapeHtml(inv.notes)}</span>
        </div>`).join("");

      // Category breakdown (pie drawn on a raw canvas → embedded as <img>)
      const catAgg = {};
      dataset.forEach((i) => {
        const cat = categoryLabel(i.category);
        catAgg[cat] = catAgg[cat] || { category: cat, total: 0, count: 0 };
        catAgg[cat].total += Number(i.total_amount || 0);
        catAgg[cat].count += 1;
      });
      const catData = Object.values(catAgg).sort((a, b) => b.total - a.total);
      const catTotal = catData.reduce((s, d) => s + d.total, 0);
      let categoryHtml = "";
      if (catTotal > 0) {
        const pieImg = drawCategoryPieImage(catData);
        const catRows = catData.map((d, i) => {
          const pct = Math.round((d.total / catTotal) * 100);
          const zebra = i % 2 === 1 ? "background:#FAF9F5;" : "background:#FFFFFF;";
          return `
            <tr style="${zebra}">
              <td style="padding:6px 8px;border:1px solid #E0DFD8;text-align:right;font-size:11px;">
                <span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${categoryHex(d.category)};margin-left:7px;vertical-align:middle;"></span>${escapeHtml(d.category)}
              </td>
              <td style="padding:6px 6px;border:1px solid #E0DFD8;text-align:center;font-variant-numeric:tabular-nums;font-size:11px;color:#5C605A;">${d.count}</td>
              <td style="padding:6px 6px;border:1px solid #E0DFD8;text-align:left;direction:ltr;font-variant-numeric:tabular-nums;font-size:11px;font-weight:600;">${fmt(d.total)}</td>
              <td style="padding:6px 6px;border:1px solid #E0DFD8;text-align:center;font-variant-numeric:tabular-nums;font-size:11px;color:#1F3A33;font-weight:600;">${pct}%</td>
            </tr>`;
        }).join("");
        categoryHtml = `
          <div style="margin-bottom:18px;">
            <div style="background:#15281F;color:#FFFFFF;padding:9px 16px;font-size:13px;font-weight:600;text-align:right;border-radius:3px 3px 0 0;">
              المصاريف حسب الصنف
            </div>
            <div style="display:flex;gap:18px;align-items:center;border:1px solid #ECEBE4;border-top:none;background:#FFFFFF;padding:14px 16px;">
              <div style="position:relative;width:215px;height:215px;flex-shrink:0;">
                <img src="${pieImg}" style="width:215px;height:215px;display:block;" />
                <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;">
                  <div style="font-size:9px;color:#8A8D86;">الإجمالي</div>
                  <div style="font-size:13px;font-weight:600;color:#1A1C19;font-variant-numeric:tabular-nums;direction:ltr;">${fmt(catTotal)}</div>
                </div>
              </div>
              <table style="flex:1;border-collapse:collapse;width:100%;">
                <thead>
                  <tr style="background:#6A8A76;color:#FFFFFF;">
                    <th style="padding:7px 8px;border:1px solid #5E7C6A;text-align:right;font-size:11px;font-weight:600;">الصنف</th>
                    <th style="padding:7px 6px;border:1px solid #5E7C6A;text-align:center;font-size:11px;font-weight:600;">عدد</th>
                    <th style="padding:7px 6px;border:1px solid #5E7C6A;text-align:center;font-size:11px;font-weight:600;">المبلغ (${escapeHtml(currency)})</th>
                    <th style="padding:7px 6px;border:1px solid #5E7C6A;text-align:center;font-size:11px;font-weight:600;">النسبة</th>
                  </tr>
                </thead>
                <tbody>${catRows}</tbody>
              </table>
            </div>
          </div>`;
      }

      root.innerHTML = `
        <!-- ============= MAIN REPORT (mirrors the Excel layout) ============= -->
        <section style="width:${W_PX}px;padding:0;box-sizing:border-box;background:#FFFFFF;">
          <!-- Row 1: Brand band (matches Excel dark green header) -->
          <div style="background:#1F3A33;color:#FFFFFF;text-align:center;padding:18px 24px;">
            <div style="font-size:22px;font-weight:600;font-feature-settings:'kern' 1,'liga' 1,'calt' 1;">فواتيري · FAWATEERY</div>
          </div>
          <!-- Row 2: Sub-title (olive band) -->
          <div style="background:#6A8A76;color:#FFFFFF;text-align:center;padding:10px 24px;font-size:13px;font-weight:500;">
            تقرير محاسبي شهري — ${escapeHtml(periodAr)}
          </div>
          <!-- Row 3: Generated info (cream band) -->
          <div style="background:#FAF9F5;color:#8A8D86;text-align:center;padding:7px 24px;font-size:11px;font-style:italic;">
            تاريخ الإصدار: ${new Date().toLocaleString("ar-SA-u-nu-latn")} · العملة: ${escapeHtml(currency)}
          </div>

          <div style="padding:18px 24px 8px 24px;">
            <!-- KPI cards row (3 cards, matches Excel rows 5-8) -->
            <div style="display:flex;gap:10px;margin-bottom:14px;">
              ${xlKpi("الرصيد المتاح (يشمل الدعم)", fmt(monthOpening), currency, "#6A8A76")}
              ${xlKpi("إجمالي المصروفات", fmt(totals.total), currency, "#C84C31")}
              ${xlKpi(
                negative ? "العجز · يُرحَّل سالباً" : "المتبقي · يُرحَّل",
                fmt(monthClosing),
                currency,
                negative ? "#C84C31" : "#1F3A33",
                negative
              )}
            </div>

            ${monthSummary ? `<div style="font-size:11px;padding:8px 0;margin-bottom:8px;">الافتتاحي: ${fmt(monthSummary.opening_base)} · الدعم: ${fmt(monthSummary.support_total || 0)} · إجمالي التمويل: ${fmt(monthSummary.funding_total ?? monthSummary.opening_base)} · المرحّل: ${fmt(monthSummary.carry_over)}</div>` : ""}
            <!-- Mini-stats strip (3 boxes, matches Excel rows 10-11) -->
            <div style="display:flex;gap:10px;margin-bottom:18px;">
              ${xlMini("عدد الفواتير", totals.count, currency, true)}
              ${xlMini("إجمالي الضريبة", fmt(totals.tax), currency)}
              ${xlMini("إجمالي الخصم", fmt(totals.disc), currency)}
            </div>

            <!-- Category breakdown (pie + table) -->
            ${categoryHtml}

            ${negative ? `
              <div style="background:#FBE9E3;border-right:4px solid #C84C31;padding:10px 14px;border-radius:4px;margin-bottom:14px;color:#7A2516;font-size:12px;">
                <strong>تنبيه:</strong> هناك عجز قدره ${fmt(Math.abs(monthClosing))} ${escapeHtml(currency)} سيُرحَّل للشهر التالي كرصيد افتتاحي سالب.
              </div>` : ""}

            <!-- Section title (matches Excel row 13 dark band) -->
            <div style="background:#15281F;color:#FFFFFF;padding:10px 16px;font-size:14px;font-weight:600;text-align:right;border-radius:3px 3px 0 0;">
              سجل الفواتير التفصيلي
            </div>

            <!-- Register table (matches Excel rows 14+) -->
            <table style="width:100%;border-collapse:collapse;font-size:11px;table-layout:fixed;">
              <colgroup>
                <col style="width:4%"/><col style="width:9%"/><col style="width:18%"/><col style="width:11%"/>
                <col style="width:10%"/><col style="width:8%"/><col style="width:8%"/><col style="width:10%"/>
                <col style="width:11%"/><col style="width:11%"/>
              </colgroup>
              <thead>
                <tr style="background:#1F3A33;color:#FFFFFF;">
                  ${["م","التاريخ","اسم المتجر","رقم الفاتورة","الإجمالي","الضريبة","الخصم","الدفع","الرصيد قبل","الرصيد بعد"]
                    .map((h, idx) => {
                      const align = (idx === 0 || idx === 1 || idx === 3 || idx === 7) ? "center"
                                  : (idx === 2) ? "right"
                                  : "left";
                      return `<th style="padding:9px 4px;text-align:${align};font-weight:600;border:1px solid #15281F;">${h}</th>`;
                    }).join("")}
                </tr>
              </thead>
              <tbody>${rowsHtml || `<tr><td colspan="10" style="padding:30px;text-align:center;color:#8A8D86;border:1px solid #E0DFD8;">لا توجد فواتير في هذه الفترة</td></tr>`}</tbody>
              <tfoot>
                <tr style="background:#1F3A33;color:#FFFFFF;font-weight:600;">
                  <td colspan="4" style="padding:10px 8px;text-align:right;border:1px solid #15281F;">الإجمالي (${totals.count} فاتورة)</td>
                  <td style="padding:10px 4px;text-align:left;direction:ltr;font-variant-numeric:tabular-nums;border:1px solid #15281F;">${fmt(totals.total)}</td>
                  <td style="padding:10px 4px;text-align:left;direction:ltr;font-variant-numeric:tabular-nums;border:1px solid #15281F;">${fmt(totals.tax)}</td>
                  <td style="padding:10px 4px;text-align:left;direction:ltr;font-variant-numeric:tabular-nums;border:1px solid #15281F;">${fmt(totals.disc)}</td>
                  <td style="padding:10px 4px;border:1px solid #15281F;"></td>
                  <td style="padding:10px 4px;border:1px solid #15281F;"></td>
                  <td style="padding:10px 4px;text-align:left;direction:ltr;font-variant-numeric:tabular-nums;border:1px solid #15281F;${negative ? "background:#C84C31;" : ""}">${fmt(monthClosing)}</td>
                </tr>
              </tfoot>
            </table>

            ${notesHtml ? `
              <div style="margin-top:16px;">
                <div style="background:#15281F;color:#FFFFFF;padding:9px 16px;font-size:13px;font-weight:600;text-align:right;border-radius:3px 3px 0 0;">
                  ملاحظات الفواتير
                </div>
                <div style="border:1px solid #ECEBE4;border-top:none;background:#FAF9F5;">
                  ${notesHtml}
                </div>
              </div>` : ""}

            <!-- Footer band (matches Excel) -->
            <div style="background:#FAF9F5;color:#8A8D86;text-align:center;padding:9px;font-size:10px;font-style:italic;margin-top:18px;border-top:1px solid #E5E4DC;">
              © فواتيري · Fawateery — تقرير مُولَّد تلقائياً
            </div>
          </div>
        </section>
      `;

      document.body.appendChild(root);

      // ============================================================
      // 2) Capture HTML → canvas, slice into A4 pages
      // ============================================================
      // Scale 1.75 keeps text crisp while ~30% smaller intermediate canvas (vs 2.0).
      // Mobile browsers (esp. iOS Safari) enforce hard canvas memory limits — use 1.5.
      const SCALE = isMobileDevice() ? 1.5 : 1.75;
      const canvas = await html2canvas(root, {
        scale: SCALE,
        backgroundColor: "#FFFFFF",
        useCORS: true,
        logging: false,
        windowWidth: W_PX,
      });
      document.body.removeChild(root);

      const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
      const PDF_W = doc.internal.pageSize.getWidth();   // 595.28
      const PDF_H = doc.internal.pageSize.getHeight();  // 841.89

      // pixel height that corresponds to one PDF page (at current canvas resolution)
      const pageHeightPx = Math.floor((PDF_H * canvas.width) / PDF_W);

      let renderedPx = 0;
      let pageIdx = 0;
      while (renderedPx < canvas.height) {
        const sliceH = Math.min(pageHeightPx, canvas.height - renderedPx);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceH;
        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, renderedPx, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        const imgData = pageCanvas.toDataURL("image/jpeg", 0.85);
        const sliceHpt = (sliceH * PDF_W) / canvas.width;
        if (pageIdx > 0) doc.addPage();
        doc.addImage(imgData, "JPEG", 0, 0, PDF_W, sliceHpt, undefined, "FAST");
        renderedPx += sliceH;
        pageIdx += 1;
      }

      // ============================================================
      // 3) Append receipt-image pages (Arabic header) — skipped in light mode
      // ============================================================
      const withImages = includeImages ? dataset.filter((i) => i.image_base64) : [];
      const margin = 40;
      for (let i = 0; i < withImages.length; i++) {
        const inv = withImages[i];
        doc.addPage();

        // Arabic header rendered via mini-HTML capture
        const hdr = document.createElement("div");
        hdr.setAttribute("dir", "rtl");
        hdr.style.cssText = `
          position:fixed;top:-10000px;right:0;width:${W_PX}px;
          background:#F5F4EF;padding:14px 22px;box-sizing:border-box;
          font-family:'IBM Plex Sans Arabic','Readex Pro','Noto Sans Arabic',Arial,sans-serif;
          font-weight:400;
        `;
        hdr.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <div style="font-size:14px;color:#1F3A33;font-weight:500;">
              ${i + 1} / ${withImages.length} · ${escapeHtml(inv.store_name || "")}
            </div>
            <div style="font-size:13px;color:#5C605A;font-variant-numeric:tabular-nums;font-weight:400;">
              ${escapeHtml(inv.invoice_date || "")} · <span style="font-weight:500;">${fmt(inv.total_amount)} ${escapeHtml(currency)}</span>
            </div>
          </div>`;
        document.body.appendChild(hdr);
        const hdrCanvas = await html2canvas(hdr, { scale: SCALE, backgroundColor: "#F5F4EF" });
        document.body.removeChild(hdr);
        const hdrHpt = (hdrCanvas.height * PDF_W) / hdrCanvas.width;
        doc.addImage(hdrCanvas.toDataURL("image/jpeg", 0.85), "JPEG", 0, 0, PDF_W, hdrHpt, undefined, "FAST");

        // Compress receipt image before embedding (huge size win, ~70% smaller)
        let compressedSrc;
        try {
          compressedSrc = await compressImageForPdf(inv.image_base64, 1400, 0.78);
          await addImageFit(doc, compressedSrc, margin, hdrHpt + 14, PDF_W - margin * 2, PDF_H - hdrHpt - 40);
        } catch (imgErr) {
          console.warn(`Skipping invoice image in PDF (${inv.id}):`, imgErr);
        }
      }

      const blob = doc.output("blob");
      deliverFile(
        blob,
        `Fawateery_${includeImages ? "" : "Light_"}${fileSuffix()}.pdf`,
        "application/pdf",
        includeImages ? "تم تصدير تقرير PDF عربي احترافي" : "تم تصدير ملخّص PDF مختصر"
      );
    } catch (e) {
      console.error(e);
      toast.error("تعذر تصدير PDF");
    } finally {
      setExporting(null);
    }
  };


  // Export ONLY receipt images of the selected month into a single PDF.
  // Each receipt = 1 page (A4), with a small header (date + store + total).
  const exportImagesOnly = async () => {
    setExporting("images");
    try {
      // Fetch full dataset with images (list page is in light mode).
      const dataset = invoices.some((i) => i.image_base64) ? invoices : await fetchInvoicesWithImages();
      const withImages = dataset.filter((i) => i.image_base64);
      if (withImages.length === 0) {
        toast.error("لا توجد صور فواتير في هذا الشهر");
        setExporting(null);
        return;
      }
      const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const margin = 36;

      // Sort by date asc for chronological order
      const sorted = [...withImages].sort((a, b) =>
        (a.invoice_date || "").localeCompare(b.invoice_date || "")
      );

      for (let i = 0; i < sorted.length; i++) {
        const inv = sorted[i];
        if (i > 0) doc.addPage();

        // Header band
        doc.setFillColor(245, 244, 239);
        doc.rect(0, 0, W, 50, "F");
        doc.setTextColor(31, 58, 51);
        doc.setFontSize(11);
        const headerLeft = `${i + 1} / ${sorted.length}`;
        const headerRight = `${monthsAr[month - 1]} ${year}`;
        doc.text(headerLeft, margin, 30);
        doc.text(headerRight, W - margin, 30, { align: "right" });

        // Meta line
        doc.setFontSize(10);
        doc.setTextColor(92, 96, 90);
        const meta = `${inv.invoice_date || ""}  |  ${(inv.store_name || "").slice(0, 30)}  |  ${Number(inv.total_amount || 0).toFixed(2)} ${currency}`;
        doc.text(meta, W / 2, 75, { align: "center" });

        // Compress receipt before embedding (≤1400px @ 0.78 JPEG)
        try {
          const compressed = await compressImageForPdf(inv.image_base64, 1400, 0.78);
          await addImageFit(doc, compressed, margin, 95, W - margin * 2, H - 130);
        } catch (imgErr) {
          console.warn(`Skipping image in PDF (${inv.id}):`, imgErr);
          doc.setTextColor(200, 76, 49);
          doc.setFontSize(12);
          doc.text("تعذر تحميل هذه الصورة", W / 2, H / 2, { align: "center" });
        }

        // Footer page number
        doc.setFontSize(9);
        doc.setTextColor(138, 141, 134);
        doc.text(`Fawateery — ${i + 1}/${sorted.length}`, W / 2, H - 18, { align: "center" });
      }

      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Fawateery_Images_${fileSuffix()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`تم تصدير ${sorted.length} صورة في ملف PDF واحد (${formatFileSize(blob.size)})`);
    } catch (e) {
      console.error(e);
      toast.error("تعذر تصدير صور الفواتير");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">التقارير</div>
        <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">تقارير شهرية</h1>
        <p className="text-[#5C605A] mt-2">صدّر ملخصاً كاملاً مع صور الفواتير لأي شهر.</p>
      </div>

      <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6 shadow-3d-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <label className="block">
            <span className="block text-sm font-medium text-[#1A1C19] mb-1.5 font-readex">الشهر</span>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value === "all" ? "all" : Number(e.target.value))}
              className="input bg-white"
              data-testid="report-month"
            >
              <option value="all">كل الأشهر</option>
              {monthsAr.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-[#1A1C19] mb-1.5 font-readex">السنة</span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value === "all" ? "all" : Number(e.target.value))}
              className="input bg-white"
              data-testid="report-year"
            >
              <option value="all">كل السنوات</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        </div>

        {/* Monthly accounting strip — only when a specific month is selected */}
        {monthSummary && month !== "all" && year !== "all" && (
          <div className="bg-gradient-to-bl from-[#1F3A33] to-[#15281F] text-white rounded-md p-5 mb-5 relative overflow-hidden shadow-3d-emerald" data-testid="monthly-balance-strip">
            <div className="absolute -bottom-12 -end-12 w-40 h-40 rounded-full bg-[#6A8A76]/20 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-white/70 font-readex mb-1">إجمالي الرصيد المتاح</div>
                <div className="font-mono-num font-medium text-2xl">{formatCurrency(monthSummary.opening_balance, currency)}</div>
                <div className="text-xs text-white/70 mt-2 space-y-1">
                  <div>الافتتاحي: {formatCurrency(monthSummary.opening_base, currency)}</div>
                  <div>الدعم: {formatCurrency(monthSummary.support_total || 0, currency)}</div>
                  <div>إجمالي التمويل: {formatCurrency(monthSummary.funding_total ?? monthSummary.opening_base, currency)}</div>
                  <div>المرحّل: {formatCurrency(monthSummary.carry_over, currency)}</div>
                </div>
                <div className="text-xs text-white/50 mt-1">{monthsAr[month - 1]} {year}</div>
              </div>
              <div>
                <div className="text-xs text-white/70 font-readex mb-1">إجمالي الإنفاق</div>
                <div className="font-mono-num font-medium text-2xl text-[#FFB4A2]">− {formatCurrency(monthSummary.total_expense, currency)}</div>
                <div className="text-xs text-white/50 mt-1">{monthSummary.count} فاتورة</div>
              </div>
              <div>
                <div className="text-xs text-white/70 font-readex mb-1">رصيد نهاية الشهر</div>
                <div className={`font-mono-num font-medium text-2xl ${monthSummary.closing_balance < 0 ? "text-[#FFB4A2]" : "text-[#A3C4B0]"}`}>{formatCurrency(monthSummary.closing_balance, currency)}</div>
                <div className="text-xs text-white/50 mt-1">{monthSummary.closing_balance >= 0 ? "يُرحَّل للشهر التالي" : "عجز يُرحَّل سالباً"}</div>
              </div>
            </div>
          </div>
        )}

        {/* Summary numbers */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Mini label="عدد الفواتير" value={totals.count} />
          <Mini label="إجمالي" value={formatCurrency(totals.total, currency)} highlight />
          <Mini label="الضريبة" value={formatCurrency(totals.tax, currency)} />
          <Mini label="الخصم" value={formatCurrency(totals.disc, currency)} />
        </div>

        {/* Category breakdown pie */}
        <div className="mb-6">
          <CategoryPie
            data={categoryBreakdown}
            currency={currency}
            title="توزيع مصاريف الفترة"
            subtitle="حسب صنف الفاتورة"
            testid="report-category-pie"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={() => exportPDF(true)}
            disabled={exporting === "pdf" || invoices.length === 0}
            data-testid="export-pdf-btn"
            className="bg-[#1F3A33] text-white hover:bg-[#172A25] active:translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-md px-5 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(31,58,51,0.18)] hover:shadow-[0_8px_20px_rgba(31,58,51,0.28)]"
          >
            {exporting === "pdf" ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <FileText className="w-5 h-5" strokeWidth={1.5} />}
            تقرير كامل PDF
          </button>

          <button
            onClick={exportImagesOnly}
            disabled={exporting === "images" || invoices.length === 0}
            data-testid="export-images-btn"
            className="bg-[#6A8A76] text-white hover:bg-[#587263] active:translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-md px-5 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(106,138,118,0.25)] hover:shadow-[0_8px_20px_rgba(106,138,118,0.35)]"
          >
            {exporting === "images" ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <Images className="w-5 h-5" strokeWidth={1.5} />}
            صور الفواتير PDF
          </button>

          <button
            onClick={() => exportPDF(false)}
            disabled={exporting === "pdf-light" || invoices.length === 0}
            data-testid="export-pdf-light-btn"
            title="تقرير مختصر بدون صور الفواتير (ملف أخف وأسرع للمشاركة)"
            className="bg-white border border-[#1F3A33]/30 hover:border-[#1F3A33] text-[#1F3A33] hover:bg-[#F0EFEB] active:translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-md px-5 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2"
          >
            {exporting === "pdf-light" ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <FileText className="w-5 h-5" strokeWidth={1.5} />}
            تقرير مختصر PDF
          </button>
        </div>

        {invoices.length === 0 && !loading && (
          <p className="text-center text-[#8A8D86] text-sm mt-5">لا توجد فواتير في هذا الشهر.</p>
        )}
      </div>

      {readyFile && <FileActionSheet file={readyFile} onClose={() => setReadyFile(null)} />}

      <style>{`
        .input { width:100%; background:white; border:1px solid rgba(26,28,25,0.15); border-radius:.375rem; padding:.6rem .85rem; font-size:.95rem; }
        .input:focus { outline:none; border-color:#1F3A33; box-shadow:0 0 0 3px rgba(31,58,51,0.12); }
      `}</style>
    </div>
  );
}

function Mini({ label, value, highlight }) {
  return (
    <div className="bg-[#F5F4EF] rounded-md p-3">
      <div className="text-xs text-[#8A8D86] font-readex mb-1">{label}</div>
      <div className={`font-mono-num font-medium text-lg ${highlight ? "text-[#1F3A33]" : "text-[#1A1C19]"}`}>{value}</div>
    </div>
  );
}

async function addImageFit(doc, dataUrl, x, y, maxW, maxH) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const ratio = Math.min(maxW / img.width, maxH / img.height);
  const w = img.width * ratio;
  const h = img.height * ratio;
  const ox = x + (maxW - w) / 2;
  const oy = y;
  doc.addImage(dataUrl, "JPEG", ox, oy, w, h, undefined, "FAST");
}

/**
 * Re-encode an image dataURL as a size-bounded JPEG for PDF embedding.
 * Receipts already arrive at ~1400px @ 0.72 (client-side compression), but
 * PDF embedding can re-grow due to base64 expansion. Re-encoding to a tight
 * JPEG keeps quality high while shrinking the resulting PDF significantly.
 *
 * @param {string} dataUrl  base64 image (data: prefixed or raw)
 * @param {number} maxWidth target max width in pixels (default 1400)
 * @param {number} quality  JPEG quality 0..1 (default 0.78 — sweet spot)
 */
async function compressImageForPdf(dataUrl, maxWidth = 1400, quality = 0.78) {
  const src = dataUrl.startsWith("data:") ? dataUrl : `data:image/jpeg;base64,${dataUrl}`;
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const scale = Math.min(1, maxWidth / img.width);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  // White background protects against any RGBA→JPEG artifacts
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, w, h);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}


// ---- HTML helpers for the Arabic PDF report ----
function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function kpiCard(label, value, currency, accent) {
  // Excel-style KPI card (rectangular, label band on top, value below)
  return `
    <div style="flex:1;border:1px solid ${accent};background:#FFFFFF;">
      <div style="background:${accent};color:#FFFFFF;text-align:center;padding:6px 8px;font-size:11px;font-weight:600;font-feature-settings:'kern' 1,'liga' 1,'calt' 1;">
        ${escapeHtml(label)}
      </div>
      <div style="background:#F5F4EF;text-align:center;padding:18px 8px;">
        <div style="font-size:22px;font-weight:600;color:#1A1C19;font-variant-numeric:tabular-nums;direction:ltr;letter-spacing:0.5px;">${escapeHtml(value)}</div>
        <div style="font-size:10px;color:#5C605A;margin-top:4px;font-weight:400;">${escapeHtml(currency)}</div>
      </div>
    </div>`;
}

const xlKpi = (label, value, currency, accent, isNegative = false) => {
  // Same as kpiCard but supports negative styling (red value on deficit).
  const valueColor = isNegative ? "#C84C31" : "#1A1C19";
  return `
    <div style="flex:1;border:1px solid ${accent};background:#FFFFFF;">
      <div style="background:${accent};color:#FFFFFF;text-align:center;padding:6px 8px;font-size:11px;font-weight:600;font-feature-settings:'kern' 1,'liga' 1,'calt' 1;">
        ${escapeHtml(label)}
      </div>
      <div style="background:#F5F4EF;text-align:center;padding:18px 8px;">
        <div style="font-size:22px;font-weight:600;color:${valueColor};font-variant-numeric:tabular-nums;direction:ltr;letter-spacing:0.5px;">${escapeHtml(value)}</div>
        <div style="font-size:10px;color:#5C605A;margin-top:4px;font-weight:400;">${escapeHtml(currency)}</div>
      </div>
    </div>`;
};

const xlMini = (label, value, currency, isCount = false) => {
  // Mini-stats box (cream background, label on top, value bigger below) — matches Excel rows 10-11.
  return `
    <div style="flex:1;border:1px solid #E0DFD8;background:#FAF9F5;">
      <div style="padding:5px 8px;font-size:9px;color:#5C605A;font-weight:600;text-align:center;">
        ${escapeHtml(label)}
      </div>
      <div style="padding:8px;text-align:center;border-top:1px solid #E0DFD8;">
        <span style="font-size:15px;font-weight:600;color:#1A1C19;font-variant-numeric:tabular-nums;direction:ltr;">${escapeHtml(String(value))}</span>
        ${isCount ? "" : `<span style="font-size:9px;color:#8A8D86;margin-inline-start:4px;">${escapeHtml(currency)}</span>`}
      </div>
    </div>`;
};

function summaryBox(label, value, currency, accent) {  return `
    <div style="flex:1;background:#F5F4EF;border-radius:8px;padding:16px 14px;position:relative;overflow:hidden;">
      <div style="position:absolute;top:0;bottom:0;right:0;width:4px;background:${accent};"></div>
      <div style="font-size:12px;color:#6B6F66;margin-bottom:10px;font-weight:500;">${escapeHtml(label)}</div>
      <div style="font-size:20px;font-weight:500;color:#1A1C19;font-variant-numeric:tabular-nums;direction:ltr;letter-spacing:0.3px;">${escapeHtml(value)}</div>
      <div style="font-size:11px;color:#8A8D86;margin-top:4px;font-weight:400;">${escapeHtml(currency)}</div>
    </div>`;
}

function kvRow(k, v) {
  return `
    <tr>
      <td style="padding:10px 4px;color:#5C605A;border-bottom:1px dashed #ECEBE4;width:40%;font-weight:400;">${escapeHtml(k)}</td>
      <td style="padding:10px 4px;color:#1A1C19;border-bottom:1px dashed #ECEBE4;font-weight:500;text-align:left;direction:ltr;">${escapeHtml(v)}</td>
    </tr>`;
}

// Draw a donut pie of category totals on a raw canvas (crisp in html2canvas/PDF).
function drawCategoryPieImage(breakdown, size = 520) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size * 0.47;
  const rInner = size * 0.28;
  const total = breakdown.reduce((s, d) => s + (d.total || 0), 0) || 1;

  let angle = -Math.PI / 2;
  breakdown.forEach((d) => {
    const sweep = Math.max((d.total / total) * Math.PI * 2, 0);
    if (sweep <= 0) return;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, rOuter, angle, angle + sweep);
    ctx.closePath();
    ctx.fillStyle = categoryHex(d.category);
    ctx.fill();
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 4;
    ctx.stroke();
    angle += sweep;
  });

  // punch the donut hole
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(cx, cy, rInner, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";

  return canvas.toDataURL("image/png");
}

